import { useTranslation } from 'react-i18next';
import { Sparkles, Search } from 'lucide-react';
import { AI_SEARCH_PROMPT_CHIPS } from '../../constants/aiSearchPrompts';

const AISearchEmptyState = ({
  variant = 'off_topic',
  summary,
  suggestions = AI_SEARCH_PROMPT_CHIPS,
  onSelectSuggestion,
}) => {
  const { t } = useTranslation();
  const isOffTopic = variant === 'off_topic';

  const headline = isOffTopic
    ? t('ai_empty_offtopic_heading', "Let's keep it about animals")
    : t('ai_empty_no_results_heading', 'Nothing to show just yet');

  const fallbackSummary = isOffTopic
    ? t('ai_empty_offtopic_body', 'I can only help with animal and pet-related questions. Try one of the prompts below to get started.')
    : t('ai_empty_no_results_body', "I couldn't find anything for that search. Try again or try one of the prompts below or rephrase your question.");

  const message = summary || fallbackSummary;

  return (
    <section
      aria-label={isOffTopic ? t('ai_empty_offtopic_aria', 'Off-topic notice') : t('ai_empty_no_results_aria', 'No results notice')}
      className="flex flex-col items-center text-center px-6 py-10 sm:py-14"
    >
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
        {isOffTopic ? (
          <Sparkles className="w-8 h-8 text-primary" aria-hidden="true" />
        ) : (
          <Search className="w-8 h-8 text-primary" aria-hidden="true" />
        )}
      </div>

      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
        {headline}
      </h2>
      <p className="text-sm text-gray-500 max-w-md leading-relaxed">
        {message}
      </p>

      {Array.isArray(suggestions) && suggestions.length > 0 ? (
        <div className="mt-6 w-full max-w-md">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
            {t('try_one_of_these', 'Try one of these')}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {suggestions.map((chip) => (
              <button
                type="button"
                key={chip}
                onClick={() =>
                  typeof onSelectSuggestion === 'function' &&
                  onSelectSuggestion(chip)
                }
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AISearchEmptyState;
